import axios from "axios";

export const API_BASE = "/api/v1";
export const SESSION_INVALID_EVENT = "campodirecto:session-invalid";
export const SESSION_UPDATED_EVENT = "campodirecto:session-updated";
const LEGACY_KEYS = ["cd_token", "cd_refresh", "cd_admin_token", "cd_admin_refresh", "cd_impersonation"];
const SIGNED_OUT_KEY = "cd_explicitly_signed_out";

// A failed logout HTTP request must never silently restore a local session.
// The marker is tab-local; normal credential login clears it.
export function markLocalSignedOut() {
    sessionStorage.setItem(SIGNED_OUT_KEY, "1");
    setAccessToken(null);
}
export function markLocalSignedIn() {
    sessionStorage.removeItem(SIGNED_OUT_KEY);
}
export function isLocalSignedOut() {
    return sessionStorage.getItem(SIGNED_OUT_KEY) === "1";
}
let memoryAccessToken = null;

function cookieValue(name) {
    if (typeof document === "undefined") return null;
    const match = document.cookie.split("; ").find((item) => item.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

export function cleanupLegacyTokenStorage() {
    for (const storage of [localStorage, sessionStorage]) {
        LEGACY_KEYS.forEach((key) => storage.removeItem(key));
    }
}

export function setAccessToken(token) {
    memoryAccessToken = token || null;
    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(SESSION_UPDATED_EVENT));
    }
}

export function getAccessToken() { return memoryAccessToken; }

export const api = axios.create({ baseURL: API_BASE, timeout: 60000, withCredentials: true });
export const authRefreshApi = axios.create({ baseURL: API_BASE, timeout: 60000, withCredentials: true });

export class AuthSessionError extends Error {
    constructor(reason) {
        super(reason === "expired" ? "session_expired" : "authentication_required");
        this.name = "AuthSessionError";
        this.reason = reason;
    }
}

function dispatchInvalidSession(reason) {
    memoryAccessToken = null;
    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(SESSION_INVALID_EVENT, { detail: { reason } }));
    }
}

function csrfHeaders(config = {}) {
    const method = (config.method || "get").toLowerCase();
    if (!["post", "put", "patch", "delete"].includes(method)) return config;
    const csrf = cookieValue("cd_csrf");
    if (csrf) {
        config.headers = config.headers || {};
        config.headers["X-CSRF-Token"] = csrf;
    }
    return config;
}

api.interceptors.request.use((config) => {
    csrfHeaders(config);
    if (memoryAccessToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${memoryAccessToken}`;
    }
    return config;
});
authRefreshApi.interceptors.request.use(csrfHeaders);

let refreshing = null;
export async function refreshAccessToken() {
    if (isLocalSignedOut()) throw new AuthSessionError("authentication_required");
    if (!refreshing) {
        refreshing = authRefreshApi.post("/auth/refresh")
            .then(({ data }) => {
                const token = data.access_token || data.token;
                if (!token) throw new AuthSessionError("expired");
                if (isLocalSignedOut()) throw new AuthSessionError("authentication_required");
                setAccessToken(token);
                return token;
            })
            .catch((error) => {
                if (error instanceof AuthSessionError || [400, 401, 403].includes(error?.response?.status)) {
                    dispatchInvalidSession("expired");
                }
                throw error;
            })
            .finally(() => { refreshing = null; });
    }
    return refreshing;
}

api.interceptors.response.use((response) => response, async (error) => {
    const original = error.config || {};
    const url = original.url || "";
    if (error?.response?.status !== 401 || original._retry || url.includes("/auth/")) {
        if (error?.response?.status === 401 && original._retry) dispatchInvalidSession("expired");
        return Promise.reject(error);
    }
    try {
        original._retry = true;
        const token = await refreshAccessToken();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
    } catch {
        return Promise.reject(error);
    }
});

export async function authenticatedRequest(config) {
    if (!memoryAccessToken) await refreshAccessToken();
    return api.request(config);
}

export const authenticatedApi = {
    request: authenticatedRequest,
    get: (url, config = {}) => authenticatedRequest({ ...config, method: "get", url }),
    post: (url, data, config = {}) => authenticatedRequest({ ...config, method: "post", url, data }),
    put: (url, data, config = {}) => authenticatedRequest({ ...config, method: "put", url, data }),
    patch: (url, data, config = {}) => authenticatedRequest({ ...config, method: "patch", url, data }),
    delete: (url, config = {}) => authenticatedRequest({ ...config, method: "delete", url }),
};

export const publicFileUrl = (fileId) => `${API_BASE}/files/public/${fileId}`;
